import React, { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../config";

const AUTH_API = `${API_BASE_URL}/auth/api/main`;
const emptyRoleForm = { name: "", description: "" };
const emptyPermForm = { name: "", description: "", parentUid: "" };
const inputStyle = { padding: "8px 10px", borderRadius: 5, border: "1px solid #DDE1E5" };

function getToken() {
    try { return JSON.parse(localStorage.getItem("user"))?.token || ""; } catch { return ""; }
}

async function api(method, path, body) {
    const response = await fetch(`${AUTH_API}${path}`, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: body ? JSON.stringify(body) : undefined,
    });
    if (response.status === 204) return null;
    if (!response.ok) throw new Error((await response.text().catch(() => "")) || `${response.status} ${response.statusText}`);
    return response.json();
}

const chipStyle = { display: "inline-block", background: "#FFF3F3", color: "#C92A2A", border: "1px solid #F1B6B6", borderRadius: 12, padding: "2px 8px", fontSize: 12, marginRight: 5, marginBottom: 5 };

// Superadmin-only RBAC UI. APIs remain hierarchy-scoped on the backend.
export default function PermissionAdmin() {
    const [roles, setRoles] = useState([]);
    const [groups, setGroups] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [users, setUsers] = useState([]);
    const [rolePerms, setRolePerms] = useState({});
    const [userPerms, setUserPerms] = useState({});
    const [activeView, setActiveView] = useState("users");
    const [selectedTag, setSelectedTag] = useState("ALL");
    const [groupName, setGroupName] = useState("");
    const [selectedUserUid, setSelectedUserUid] = useState("");
    const [permissionSearch, setPermissionSearch] = useState("");
    const [roleForm, setRoleForm] = useState(emptyRoleForm);
    const [permForm, setPermForm] = useState(emptyPermForm);
    const [assignRoleUid, setAssignRoleUid] = useState("");
    const [assignRolePermUid, setAssignRolePermUid] = useState("");
    const [editingRole, setEditingRole] = useState(null);
    const [editRoleName, setEditRoleName] = useState("");
    const [changingUserRole, setChangingUserRole] = useState(null);
    const [error, setError] = useState("");
    const [msg, setMsg] = useState("");
    const [loading, setLoading] = useState(false);
    const [effective, setEffective] = useState(null);
    const [effectiveLoading, setEffectiveLoading] = useState(false);

    const load = useCallback(async () => {
        setError("");
        try {
            const [roleList, permissionList, userList, groupList] = await Promise.all([
                api("GET", "/roles/same-or-lower"),
                api("GET", "/permissions/role/children"),
                api("GET", "/users/same-or-lower"),
                api("GET", "/tags"),
            ]);
            setRoles(roleList || []);
            setPermissions(permissionList || []);
            setUsers(userList || []);
            setGroups((groupList || []).map(group => ({ id: group.id || group.uid, name: group.name || group.tagname })).filter(group => group.id && group.name));
            const roleEntries = await Promise.all((roleList || []).map(role =>
                api("GET", `/roles/${encodeURIComponent(role.id)}/permissions`)
                    .then(list => [role.id, (list || []).map(item => item.name)])
                    .catch(() => [role.id, []])
            ));
            const userEntries = await Promise.all((userList || []).map(user =>
                api("GET", `/users/${encodeURIComponent(user.uid)}/permissions`)
                    .then(list => [user.uid, (list || []).map(item => item.name)])
                    .catch(() => [user.uid, []])
            ));
            setRolePerms(Object.fromEntries(roleEntries));
            setUserPerms(Object.fromEntries(userEntries));
        } catch (e) { setError(e.message || "โหลดไม่สำเร็จ"); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const refreshEffective = useCallback(() => {
        if (!selectedUserUid) { setEffective(null); return; }
        setEffectiveLoading(true);
        api("GET", `/users/${encodeURIComponent(selectedUserUid)}/effective-permissions`)
            .then(setEffective)
            .catch(() => setEffective(null))
            .finally(() => setEffectiveLoading(false));
    }, [selectedUserUid]);

    useEffect(() => { refreshEffective(); }, [refreshEffective]);

    async function runAction(action, successMessage) {
        setLoading(true); setError(""); setMsg("");
        try { await action(); setMsg(successMessage); await load(); await refreshEffective(); }
        catch (e) { setError(e.message || "ทำรายการไม่สำเร็จ"); }
        finally { setLoading(false); }
    }

    function createRole() {
        if (!roleForm.name.trim()) return setError("กรุณากรอกชื่อ Role");
        return runAction(() => api("POST", "/roles/same-or-lower", {
            name: roleForm.name.trim(), description: roleForm.description.trim(),
        }), "สร้าง Role สำเร็จ").then(() => setRoleForm(emptyRoleForm));
    }

    function createPermission() {
        if (!permForm.name.trim() || !permForm.name.includes(":")) return setError("กรุณากรอกชื่อ Permission แบบ resource:action");
        return runAction(() => api("POST", "/permissions", {
            name: permForm.name.trim(), description: permForm.description.trim(), parentUid: permForm.parentUid || null,
        }), "สร้าง Permission สำเร็จ").then(() => setPermForm(emptyPermForm));
    }

    function assignToRole() {
        if (!assignRoleUid || !assignRolePermUid) return setError("กรุณาเลือก Role และ Permission");
        const permission = permissions.find(item => item.id === assignRolePermUid);
        return runAction(() => api("POST", `/roles/${encodeURIComponent(assignRoleUid)}/permissions${permission?.name?.endsWith(":*") ? "/wildcard" : ""}`, { permissionUid: assignRolePermUid }), `เพิ่ม ${permission?.name} ให้ Role สำเร็จ`);
    }

    function assignToUser(user, permission) {
        return runAction(() => api("POST", `/users/${encodeURIComponent(user.uid)}/permissions${permission.name?.endsWith(":*") ? "/wildcard" : ""}`, { permissionUid: permission.id }), `เพิ่ม ${permission.name} ให้ ${user.nameuser || user.username} สำเร็จ`);
    }

    function createGroup() {
        if (!groupName.trim()) return setError("กรุณากรอกชื่อกลุ่ม");
        return runAction(() => api("POST", "/tags", { tagname: groupName.trim() }), "สร้างกลุ่มสำเร็จ")
            .then(() => setGroupName(""));
    }

    function assignGroupToUser(user, group) {
        return runAction(() => api("POST", `/users/${encodeURIComponent(user.uid)}/tags`, { tagUid: group.id }), `เพิ่ม ${user.nameuser || user.username} เข้ากลุ่ม ${group.name} สำเร็จ`);
    }

    async function renameRole() {
        if (!editRoleName.trim()) return setError("กรุณากรอกชื่อ Role");
        setLoading(true); setError(""); setMsg("");
        try {
            await api("PUT", `/roles/${encodeURIComponent(editingRole.id)}`, { name: editRoleName.trim() });
            setEditingRole(null);
            setMsg("แก้ไขชื่อ Role สำเร็จ");
            await load();
        } catch (e) { setError(e.message || "แก้ไขชื่อ Role ไม่สำเร็จ"); }
        finally { setLoading(false); }
    }

    async function changeUserRole(role) {
        if (!changingUserRole || role.id === changingUserRole.roleUid) return setChangingUserRole(null);
        setLoading(true); setError(""); setMsg("");
        try {
            await api("PUT", `/users/${encodeURIComponent(changingUserRole.uid)}/role`, { roleUid: role.id });
            setChangingUserRole(null);
            setMsg(`เปลี่ยน Role ของ ${changingUserRole.nameuser || changingUserRole.username} เป็น ${role.name} สำเร็จ`);
            await load();
            await refreshEffective();
        } catch (e) { setError(e.message || "เปลี่ยน Role ไม่สำเร็จ"); }
        finally { setLoading(false); }
    }

    const tags = [...new Set(users.flatMap(user => Array.isArray(user.tags) ? user.tags : []))].sort();
    const visibleUsers = users.filter(user => selectedTag === "ALL" || (Array.isArray(user.tags) && user.tags.includes(selectedTag)))
        .sort((a, b) => (a.nameuser || a.username || "").localeCompare(b.nameuser || b.username || ""));
    const selectedUser = users.find(user => user.uid === selectedUserUid);
    const selectedUserPermissions = selectedUser ? userPerms[selectedUser.uid] || [] : [];
    const matchingPermissions = permissions.filter(permission => permission.name.toLowerCase().includes(permissionSearch.trim().toLowerCase()));
    const tabStyle = tab => ({ background: activeView === tab ? "#343A40" : "#F1F3F5", color: activeView === tab ? "#FFF" : "#343A40", border: "none", borderRadius: 6, padding: "9px 14px", fontWeight: 600 });

    return (
        <div className="btc-container">
            <div style={{ marginBottom: 16 }}>
                <h1 style={{ marginBottom: 4 }}>การกำหนดสิทธิ์</h1>
                <p style={{ margin: 0, color: "#6B6B6B" }}>จัดการสิทธิ์เฉพาะคน, Role และ Permission แยกกันเพื่อให้ใช้งานง่าย</p>
            </div>
            {error && <p className="error">{error}</p>}
            {msg && <p style={{ color: "#218838" }}>{msg}</p>}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
                <button style={tabStyle("users")} onClick={() => { setActiveView("users"); setSelectedUserUid(""); }}>ผู้ใช้</button>
                <button style={tabStyle("groups")} onClick={() => setActiveView("groups")}>กลุ่มผู้ใช้</button>
                <button style={tabStyle("roles")} onClick={() => setActiveView("roles")}>Roles</button>
                <button style={tabStyle("permissions")} onClick={() => setActiveView("permissions")}>Permission catalog</button>
            </div>

            {activeView === "users" && !selectedUser && <div className="btc-card" style={{ padding: "18px 22px" }}>
                <h3 style={{ marginTop: 0 }}>ผู้ใช้ ({visibleUsers.length})</h3>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                    {["ALL", ...tags].map(tag => <button key={tag} className="btc-btn btn-sm" onClick={() => setSelectedTag(tag)}
                        style={{ background: selectedTag === tag ? "#343A40" : "#F1F3F5", color: selectedTag === tag ? "#FFF" : "#343A40" }}>
                        {tag === "ALL" ? `ทั้งหมด (${users.length})` : `${tag} (${users.filter(user => Array.isArray(user.tags) && user.tags.includes(tag)).length})`}
                    </button>)}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
                    {visibleUsers.map(user => <button key={user.uid} onClick={() => { setSelectedUserUid(user.uid); setPermissionSearch(""); }}
                        style={{ textAlign: "left", padding: 15, border: "1px solid #DDE1E5", borderRadius: 8, background: "#FFF", cursor: "pointer" }}>
                        <strong>{user.nameuser || user.username}</strong>
                        <div style={{ color: "#6B6B6B", fontSize: 13, marginTop: 4 }}>{user.username} · {user.roleName || "ไม่มี Role"}</div>
                        {Array.isArray(user.tags) && user.tags.length > 0 && <div style={{ color: "#6B6B6B", fontSize: 12, marginTop: 5 }}>กลุ่ม: {user.tags.join(", ")}</div>}
                        <div style={{ color: "#6B6B6B", fontSize: 12, marginTop: 8 }}>Direct permission: {(userPerms[user.uid] || []).length} รายการ</div>
                    </button>)}
                </div>
            </div>}

            {activeView === "users" && selectedUser && <div className="btc-card" style={{ padding: "18px 22px", maxWidth: 900 }}>
                <button className="btc-btn btn-sm" onClick={() => setSelectedUserUid("")} style={{ background: "#F1F3F5", color: "#343A40", marginBottom: 14 }}>← กลับรายชื่อผู้ใช้</button>
                <h3 style={{ margin: "0 0 4px" }}>{selectedUser.nameuser || selectedUser.username}</h3>
                <p style={{ marginTop: 0, color: "#6B6B6B" }}>{selectedUser.username} · {selectedUser.roleName || "ไม่มี Role"} · Direct grant เท่านั้น</p>
                <button className="btc-btn btn-sm" onClick={() => setChangingUserRole(selectedUser)} disabled={loading}
                    style={{ background: "#F1F3F5", color: "#343A40", marginBottom: 16 }}>เปลี่ยน Role</button>
                <div style={{ marginBottom: 16 }}>
                    {selectedUserPermissions.length === 0 ? <span style={{ color: "#6B6B6B" }}>ยังไม่มี direct permission</span> : selectedUserPermissions.map(name => <span key={name} style={chipStyle}>{name}</span>)}
                </div>
                <div style={{ borderTop: "1px solid #E9ECEF", paddingTop: 14, marginBottom: 16, background: "#FAFBFC", borderRadius: 8, padding: 14 }}>
                    <strong style={{ display: "block", marginBottom: 8 }}>สิทธิ์ที่มีผลจริง (Role + กลุ่ม + Direct)</strong>
                    <p style={{ margin: "0 0 10px", color: "#6B6B6B", fontSize: 12 }}>เอาไว้เช็คว่าสิทธิ์แต่ละอันมาจากไหน — Role ตรง ๆ, Direct grant, หรือมาจากกลุ่มที่ผูก permission ไว้</p>
                    {effectiveLoading ? <span style={{ color: "#6B6B6B", fontSize: 13 }}>กำลังโหลด...</span> : !effective ? <span style={{ color: "#6B6B6B", fontSize: 13 }}>โหลดไม่สำเร็จ</span> : <>
                        <div style={{ marginBottom: 8 }}>
                            <span style={{ color: "#6B6B6B", fontSize: 12 }}>จาก Role ({effective.roleName || "ไม่มี Role"}):</span>{" "}
                            {(effective.rolePermissions || []).length ? effective.rolePermissions.map(name => <span key={`r-${name}`} style={chipStyle}>{name}</span>) : <span style={{ color: "#ADB5BD", fontSize: 12 }}>—</span>}
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <span style={{ color: "#6B6B6B", fontSize: 12 }}>Direct:</span>{" "}
                            {(effective.directPermissions || []).length ? effective.directPermissions.map(name => <span key={`d-${name}`} style={chipStyle}>{name}</span>) : <span style={{ color: "#ADB5BD", fontSize: 12 }}>—</span>}
                        </div>
                        {Object.entries(effective.tagPermissions || {}).filter(([, names]) => (names || []).length > 0).map(([tagname, names]) => (
                            <div key={tagname} style={{ marginBottom: 8 }}>
                                <span style={{ color: "#6B6B6B", fontSize: 12 }}>จากกลุ่ม "{tagname}":</span>{" "}
                                {names.map(name => <span key={`t-${tagname}-${name}`} style={{ ...chipStyle, background: "#EEF3FF", color: "#2952CC", border: "1px solid #C7D6F5" }}>{name}</span>)}
                            </div>
                        ))}
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #DDE1E5" }}>
                            <span style={{ color: "#6B6B6B", fontSize: 12 }}>รวมทั้งหมด ({(effective.combined || []).length}):</span>{" "}
                            {(effective.combined || []).length ? effective.combined.map(name => <span key={`c-${name}`} style={{ ...chipStyle, background: "#F1F3F5", color: "#343A40", border: "1px solid #DDE1E5" }}>{name}</span>) : <span style={{ color: "#ADB5BD", fontSize: 12 }}>—</span>}
                        </div>
                    </>}
                </div>
                <div style={{ borderTop: "1px solid #E9ECEF", paddingTop: 14, marginBottom: 16 }}>
                    <strong style={{ display: "block", marginBottom: 8 }}>กลุ่มผู้ใช้</strong>
                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                        {groups.map(group => {
                            const assigned = Array.isArray(selectedUser.tags) && selectedUser.tags.includes(group.name);
                            return <button key={group.id} className="btc-btn btn-sm" disabled={loading || assigned} onClick={() => assignGroupToUser(selectedUser, group)}
                                style={{ background: assigned ? "#E9ECEF" : "#343A40", color: assigned ? "#6B6B6B" : "#FFF" }}>{assigned ? "✓ " : "+ "}{group.name}</button>;
                        })}
                    </div>
                </div>
                <input value={permissionSearch} onChange={event => setPermissionSearch(event.target.value)} placeholder="ค้นหา permission เช่น saledata หรือ :read" style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: 12 }} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8 }}>
                    {matchingPermissions.map(permission => {
                        const granted = selectedUserPermissions.includes(permission.name);
                        return <button key={permission.id} className="btc-btn btn-sm" disabled={loading || granted} onClick={() => assignToUser(selectedUser, permission)}
                            style={{ textAlign: "left", background: granted ? "#FCEBEC" : "#343A40", color: granted ? "#C92A2A" : "#FFF", border: granted ? "1px solid #F1B6B6" : "none" }}>
                            {granted ? "✓ " : "+ "}{permission.name}
                        </button>;
                    })}
                </div>
            </div>}

            {activeView === "groups" && <div className="btc-card" style={{ padding: "18px 22px", maxWidth: 900 }}>
                <h3 style={{ marginTop: 0 }}>กลุ่มผู้ใช้</h3>
                <p style={{ color: "#6B6B6B", marginTop: 0 }}>ใช้จัดหมวดผู้ใช้ เช่น สายรถ, บัญชี หรือคลังสินค้า</p>
                <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
                    <input value={groupName} onChange={event => setGroupName(event.target.value)} onKeyDown={event => event.key === "Enter" && createGroup()} placeholder="ชื่อกลุ่มใหม่" style={{ ...inputStyle, minWidth: 220, flex: 1 }} />
                    <button className="btc-btn btn-sm" onClick={createGroup} disabled={loading} style={{ background: "#343A40" }}>+ สร้างกลุ่ม</button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
                    {groups.map(group => <div key={group.id} style={{ border: "1px solid #DDE1E5", borderRadius: 8, padding: 14 }}>
                        <strong>{group.name}</strong>
                        <div style={{ color: "#6B6B6B", fontSize: 12, marginTop: 6 }}>{users.filter(user => Array.isArray(user.tags) && user.tags.includes(group.name)).length} ผู้ใช้</div>
                    </div>)}
                    {groups.length === 0 && <span style={{ color: "#6B6B6B" }}>ยังไม่มีกลุ่ม</span>}
                </div>
            </div>}

            {activeView === "roles" && <>
                <div className="btc-card" style={{ padding: "18px 22px" }}>
                    <h3 style={{ marginTop: 0 }}>Roles ({roles.length})</h3>
                    <div style={{ overflowX: "auto" }}>
                        <table className="btc-table" style={{ width: "100%", minWidth: 620, fontSize: 13 }}>
                            <thead><tr><th>Role</th><th>Permissions</th><th style={{ width: 90 }}>จัดการ</th></tr></thead>
                            <tbody>{roles.map(role => <tr key={role.id}>
                                <td><strong>{role.name}</strong>{role.description && <div style={{ color: "#6B6B6B", fontSize: 12, marginTop: 3 }}>{role.description}</div>}</td>
                                <td>{(rolePerms[role.id] || []).length ? (rolePerms[role.id] || []).map(name => <span key={name} style={chipStyle}>{name}</span>) : <span style={{ color: "#6B6B6B", fontSize: 12 }}>ไม่มี permission</span>}</td>
                                <td><button className="btc-btn btn-sm" onClick={() => { setEditingRole(role); setEditRoleName(role.name); }} style={{ background: "#F1F3F5", color: "#343A40" }}>แก้ไข</button></td>
                            </tr>)}</tbody>
                        </table>
                    </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 16, marginTop: 16 }}>
                    <div className="btc-card" style={{ padding: "18px 22px" }}>
                        <h3 style={{ marginTop: 0 }}>สร้าง Role</h3>
                        <div style={{ display: "grid", gap: 8 }}>
                        <input value={roleForm.name} onChange={e => setRoleForm(form => ({ ...form, name: e.target.value }))} placeholder="ชื่อ Role" style={inputStyle} />
                        <input value={roleForm.description} onChange={e => setRoleForm(form => ({ ...form, description: e.target.value }))} placeholder="คำอธิบาย" style={inputStyle} />
                        <button className="btc-btn btn-sm" onClick={createRole} disabled={loading} style={{ background: "#343A40" }}>+ สร้าง Role</button>
                        </div>
                    </div>
                    <div className="btc-card" style={{ padding: "18px 22px" }}>
                        <h3 style={{ marginTop: 0 }}>เพิ่ม Permission ให้ Role</h3>
                        <div style={{ display: "grid", gap: 8 }}>
                        <select value={assignRoleUid} onChange={e => setAssignRoleUid(e.target.value)} style={inputStyle}><option value="">เลือก Role</option>{roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}</select>
                        <select value={assignRolePermUid} onChange={e => setAssignRolePermUid(e.target.value)} style={inputStyle}><option value="">เลือก Permission</option>{permissions.map(permission => <option key={permission.id} value={permission.id}>{permission.name}</option>)}</select>
                        <button className="btc-btn btn-sm" onClick={assignToRole} disabled={loading} style={{ background: "#343A40" }}>เพิ่ม Permission ให้ Role</button>
                        </div>
                    </div>
                </div>
            </>}

            {editingRole && <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0, 0, 0, 0.35)", display: "grid", placeItems: "center", padding: 16 }}>
                <div className="btc-card" style={{ width: "min(460px, 100%)", padding: "22px 24px", background: "#FFF" }}>
                    <h3 style={{ marginTop: 0 }}>แก้ไขชื่อ Role</h3>
                    <input value={editRoleName} onChange={event => setEditRoleName(event.target.value)} placeholder="ชื่อ Role" style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} autoFocus />
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
                        <button className="btc-btn btn-sm" onClick={() => setEditingRole(null)} disabled={loading} style={{ background: "#F1F3F5", color: "#343A40" }}>ยกเลิก</button>
                        <button className="btc-btn btn-sm" onClick={renameRole} disabled={loading} style={{ background: "#343A40" }}>บันทึก</button>
                    </div>
                </div>
            </div>}

            {changingUserRole && <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0, 0, 0, 0.35)", display: "grid", placeItems: "center", padding: 16 }}>
                <div className="btc-card" style={{ width: "min(620px, 100%)", padding: "22px 24px", background: "#FFF" }}>
                    <h3 style={{ marginTop: 0 }}>เปลี่ยน Role</h3>
                    <p style={{ color: "#6B6B6B", marginTop: 0 }}>ผู้ใช้: <strong>{changingUserRole.nameuser || changingUserRole.username}</strong> · Role ปัจจุบัน: <strong>{changingUserRole.roleName || "ไม่มี Role"}</strong></p>
                    <p style={{ color: "#C92A2A", fontSize: 13 }}>เฉพาะ SuperAdmin เท่านั้นที่เปลี่ยน Role ได้</p>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8 }}>
                        {roles.map(role => {
                            const current = role.id === changingUserRole.roleUid;
                            return <button key={role.id} className="btc-btn btn-sm" disabled={loading || current} onClick={() => changeUserRole(role)}
                                style={{ textAlign: "left", background: current ? "#E9ECEF" : "#343A40", color: current ? "#6B6B6B" : "#FFF" }}>
                                {current ? "✓ " : ""}{role.name}
                            </button>;
                        })}
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                        <button className="btc-btn btn-sm" onClick={() => setChangingUserRole(null)} disabled={loading} style={{ background: "#F1F3F5", color: "#343A40" }}>ยกเลิก</button>
                    </div>
                </div>
            </div>}

            {activeView === "permissions" && <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 360px) minmax(0, 1fr)", gap: 16 }}>
                <div className="btc-card" style={{ padding: "18px 22px" }}><h3 style={{ marginTop: 0 }}>สร้าง Permission</h3><div style={{ display: "grid", gap: 8 }}>
                    <input value={permForm.name} onChange={e => setPermForm(form => ({ ...form, name: e.target.value }))} placeholder="resource:action" style={inputStyle} />
                    <input value={permForm.description} onChange={e => setPermForm(form => ({ ...form, description: e.target.value }))} placeholder="คำอธิบาย" style={inputStyle} />
                    <select value={permForm.parentUid} onChange={e => setPermForm(form => ({ ...form, parentUid: e.target.value }))} style={inputStyle}><option value="">Parent อัตโนมัติ</option>{permissions.map(permission => <option key={permission.id} value={permission.id}>{permission.name}</option>)}</select>
                    <button className="btc-btn btn-sm" onClick={createPermission} disabled={loading} style={{ background: "#343A40" }}>+ สร้าง Permission</button>
                </div></div>
                <div className="btc-card" style={{ padding: "18px 22px" }}><h3 style={{ marginTop: 0 }}>Permission ทั้งหมด ({permissions.length})</h3><div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>{permissions.map(permission => <span key={permission.id} style={{ background: "#F1F3F5", color: "#343A40", borderRadius: 12, padding: "4px 9px", fontSize: 13 }}>{permission.name}</span>)}</div></div>
            </div>}
        </div>
    );
}
